import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import Header from '../components/HeaderComponent/Header';
import { FirebaseProvider } from '../providers/Firebase';
import { getCachedTokenInformation, getTokenInformation, redirectToCSAT, registerInspectionHistory } from '../services/legacyService';
import { EventType } from '../shared/enums';
import './Feedback.css';

export default function Feedback() {
  const firebaseProvider = useMemo(() => new FirebaseProvider(), []);
  const navigate = useNavigate();
  const location = useLocation();
  
  const urlParams = new URLSearchParams(window.location.search);
  const isFinishedViaUrl = urlParams.get('isFinished') === 'true';  
  const state = location.state || {}; 
  
  const sessionId = state.sessionId || urlParams.get('token');
  const insuranceId = state.insuranceId || urlParams.get('ins');
  const userId = state.userId || urlParams.get('usr');
  const isFinishedViaState = state.isFinished;
  const normalizeInsurerValue = (value: string) =>
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  const isBancoDoBrasilValue = (value: string) => {
    const normalizedValue = normalizeInsurerValue(value);
    return (
      normalizedValue.includes('banco do brasil') ||
      normalizedValue.includes('bb seguros') ||
      normalizedValue.includes('bb seguridade') ||
      normalizedValue.includes('brasilseg') ||
      /\bbb\b/.test(normalizedValue)
    );
  };
  const isMapfreValue = (value: string) =>
    normalizeInsurerValue(value).includes('mapfre');
  const hasInsurerInCustomerData = (
    customerData: unknown,
    predicate: (value: string) => boolean
  ) =>
    !!customerData &&
    typeof customerData === 'object' &&
    Object.values(customerData as Record<string, unknown>).some(
      (value) => typeof value === 'string' && predicate(value)
    );
  const cachedCustomerData = getCachedTokenInformation(sessionId || undefined);
  const [isBancoDoBrasil, setIsBancoDoBrasil] = useState(
    isBancoDoBrasilValue(String(insuranceId || '')) ||
      hasInsurerInCustomerData(cachedCustomerData, isBancoDoBrasilValue)
  );
  const [isMapfre, setIsMapfre] = useState(
    isMapfreValue(String(insuranceId || '')) ||
      hasInsurerInCustomerData(cachedCustomerData, isMapfreValue)
  );

  // lógica de exibição do botão de retorno
  const hideBackButton = isFinishedViaUrl === true || isFinishedViaState === true;

  useEffect(() => {
    firebaseProvider.sendUserEvent(EventType.PAGE_VIEW, {
      page_location: '/feedback',
      page_encoding: '',
      page_title: 'Chamada Finalizada',
      user_agent: '',
      client_id: sessionId || '',
      language: '',
    });

    firebaseProvider.sendUserEvent(EventType.LEVEL_END, {
      level_name: hideBackButton 
        ? 'Vistoria finalizada pelo perito - Reingresso negado' 
        : 'Fluxo de teleconferência pausado pelo usuário',
    });
  }, [firebaseProvider, sessionId, hideBackButton]);

  useEffect(() => {
    let isMounted = true;

    if (!sessionId || isBancoDoBrasil || isMapfre) {
      return () => {
        isMounted = false;
      };
    }

    const loadCustomerInformation = async () => {
      try {
        const response = await getTokenInformation(sessionId);
        const customerData = Array.isArray(response) ? response[0] : response;
        const hasBancoDoBrasilData = hasInsurerInCustomerData(customerData, isBancoDoBrasilValue);
        const hasMapfreData = hasInsurerInCustomerData(customerData, isMapfreValue);

        if (isMounted) {
          if (hasBancoDoBrasilData) setIsBancoDoBrasil(true);
          if (hasMapfreData) setIsMapfre(true);
        }
      } catch (error) {
        console.log(error);
      }
    };

    loadCustomerInformation();

    return () => {
      isMounted = false;
    };
  }, [sessionId, isBancoDoBrasil, isMapfre]);

  const avaliarButtonClass = isBancoDoBrasil
    ? 'btn-avaliar-bb'
    : isMapfre
      ? 'btn-avaliar-mapfre'
      : '';
  const voltarButtonClass = isBancoDoBrasil
    ? 'btn-voltar-bb'
    : isMapfre
      ? 'btn-voltar-mapfre'
      : '';

  const handleVoltar = async () => {
    // Registra a ação no Hub
    await registerInspectionHistory({
      inspectionId: sessionId || '',
      nameUser: 'Integração',
      message: 'Segurado/usuário clicou no botão de voltar para chamada',
      companyPersonId: undefined,
    });
    
    navigate(-1);
  };

  const handleAvaliar = async () => {
    // Registra a ação no Hub
    await registerInspectionHistory({
      inspectionId: sessionId || '',
      nameUser: 'Integração',
      message: 'Segurado/usuário clicou no botão de avaliar da chamada',
      companyPersonId: undefined,
    });

    // Agora as variáveis insuranceId e userId estão protegidas pelo fallback da URL
    console.log("Encerrando via CSAT", { insuranceId, userId });
    redirectToCSAT(insuranceId, userId);
  };

  return (
    <div className="feedback-container">
      {/* O token garante que o logo seja dinâmico conforme a seguradora do contexto */}
      <Header token={sessionId!} />
      
      <header className="App-header">
        <div className="feedbackMessage">          
          <div className="check-wrapper">
             <div className="check-icon">✓</div>
          </div>

          <h2>Chamada Finalizada</h2>         

          <div className="feedback-actions">
            <Button 
              variant="contained" 
              className={`btn-avaliar ${avaliarButtonClass}`.trim()}
              onClick={handleAvaliar}
              fullWidth
            >
              Avaliar chamada
            </Button>

            {/* Exibe o botão de retorno apenas se a vistoria NÃO estiver encerrada */}
            {!hideBackButton && (
              <Button 
                variant="text" 
                className={`btn-voltar ${voltarButtonClass}`.trim()} 
                onClick={handleVoltar}
                fullWidth
              >
                Voltar para chamada
              </Button>
            )}
          </div>
        </div>
      </header>
    </div>
  );
}
